import React, { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { fmtCheck, INTERNAL_LABEL } from "@/lib/connection";
import { DisconnectModal } from "@/components/DisconnectModal";
import * as Icons from "lucide-react";
import { HelperSetup } from "@/components/HelperSetup";

export const ConnectionPanel = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [showDisc, setShowDisc] = useState(false);
  const [info, setInfo] = useState("");

  const load = useCallback(async () => setSt((await api.get("/session/status")).data), []);
  useEffect(() => {
    load();
    window.addEventListener("focus", load);
    return () => window.removeEventListener("focus", load);
  }, [load]);

  const check = async () => {
    setBusy(true); setErr(""); setInfo("");
    try { await api.post("/session/check"); }
    catch (e) { setErr(e.response?.status === 429 ? "Please wait a few seconds before checking again." : "Check failed."); }
    setBusy(false); load();
  };

  if (!st) return null;
  const internal = INTERNAL_LABEL[st.internal.state] || INTERNAL_LABEL.disconnected;
  return (
    <div className="mt-8 bg-white border border-stone-200 rounded-2xl p-5" data-testid="connection-panel">
      <div className="text-xs uppercase tracking-wider text-stone-400 mb-3">FUB Connections</div>
      <div className="grid sm:grid-cols-2 gap-3 text-sm">
        <div>Official FUB API: <span data-testid="official-status" className={st.official.connected ? "text-emerald-700 font-medium" : "text-rose-700 font-medium"}>{st.official.connected ? "Connected" : "Problem"}</span></div>
        <div>Internal FUB Configuration: <span data-testid="internal-status" className={`font-medium ${internal.cls}`}>{internal.text}</span></div>
      </div>
      <div className="text-xs text-stone-500 mt-2" data-testid="last-checked">Last checked: {fmtCheck(st.internal.last_verified)}</div>
      <div className="text-xs text-stone-500" data-testid="session-stored">Stored internal session: {st.internal.configured ? "Yes" : "None"}</div>
      {st.internal.last_validation && (
        <div className="text-xs text-stone-500 font-mono mt-1" data-testid="last-validation">
          Last validation: HTTP {String(st.internal.last_validation.status)} · {st.internal.last_validation.content_type || "no content-type"}
          {st.internal.last_validation.redirect && ` · redirect ${st.internal.last_validation.redirect.status} → ${st.internal.last_validation.redirect.location}`}
          {` · server ${st.internal.last_validation.server || "—"} · cf-ray ${st.internal.last_validation.cf_ray_present ? "present" : "absent"}`}
        </div>
      )}
      {err && <div className="text-xs text-rose-700 mt-2" data-testid="connection-error">{err}</div>}
      {info && <div className="text-xs text-emerald-700 mt-2" data-testid="disconnect-success">{info}</div>}
      <div className="flex gap-2 mt-4">
        <button onClick={check} disabled={busy} data-testid="check-connection-btn"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-stone-300 rounded-lg hover:bg-stone-100 disabled:opacity-50">
          <Icons.RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Check Connection
        </button>
        <button onClick={() => setShowDisc(true)} data-testid="disconnect-btn"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-rose-300 text-rose-700 rounded-lg hover:bg-rose-50">
          <Icons.Unplug className="w-4 h-4" /> Disconnect
        </button>
      </div>
      <HelperSetup approvedNames={st.internal.approved_cookie_names} />
      {showDisc && <DisconnectModal onClose={() => setShowDisc(false)} onDone={(msg) => { setShowDisc(false); setErr(""); setInfo(msg); load(); }} />}
    </div>
  );
};
